import { useEffect, useMemo, useState } from "react";
import { Plus, Receipt, Loader2, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { toast } from "sonner";

// Grupos de categorías (label legible → key backend)
const CATEGORIAS_GRUPOS = [
  {
    label: "Personal",
    items: [
      { key: "personal_sueldos", label: "Sueldos y salarios (5101)" },
      { key: "personal_prestaciones", label: "Prestaciones al personal (5102)" },
      { key: "personal_honorarios", label: "Honorarios profesionales (5103)" },
      { key: "personal_capacitacion", label: "Capacitación (5104)" },
    ],
  },
  {
    label: "Operación",
    items: [
      { key: "operacion_renta", label: "Renta de oficinas (5201)" },
      { key: "operacion_servicios", label: "Servicios (luz/agua/internet/tel) (5202)" },
      { key: "operacion_papeleria", label: "Papelería y consumibles (5203)" },
      { key: "operacion_mantenimiento", label: "Mantenimiento y limpieza (5204)" },
      { key: "operacion_software", label: "Software y sistemas (5205)" },
    ],
  },
  {
    label: "Comercial",
    items: [
      { key: "comercial_publicidad", label: "Publicidad y promoción (5301)" },
      { key: "comercial_comisiones", label: "Comisiones a promotores (5302)" },
    ],
  },
  {
    label: "Administrativo",
    items: [
      { key: "admin_contabilidad", label: "Contabilidad y auditoría externa (5401)" },
      { key: "admin_legales", label: "Honorarios legales (5402)" },
      { key: "admin_regulatorias", label: "Cuotas regulatorias CNBV/CONDUSEF (5403)" },
      { key: "admin_seguros", label: "Seguros (5404)" },
    ],
  },
  {
    label: "Financiero",
    items: [
      { key: "financiero_interes_fondeo", label: "Intereses sobre fondeo recibido (5501)" },
      { key: "financiero_comisiones_banco", label: "Comisiones bancarias (5502)" },
    ],
  },
  {
    label: "Otros",
    items: [{ key: "otros", label: "Otros gastos operativos (5901)" }],
  },
];

const CATEGORIA_LABEL = {};
CATEGORIAS_GRUPOS.forEach((g) => g.items.forEach((i) => { CATEGORIA_LABEL[i.key] = i.label; }));

const FORMAS = [
  { value: "transferencia", label: "Transferencia (Bancos 1102)" },
  { value: "efectivo", label: "Efectivo (Caja 1101)" },
];

function DialogNuevoGasto({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    categoria: "",
    monto: 0,
    fecha: new Date().toISOString().slice(0, 10),
    forma: "transferencia",
    banco_origen: "",
    proveedor: "",
    descripcion: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!form.categoria) { toast.error("Selecciona la categoría del gasto."); return; }
    if (!(form.monto > 0)) { toast.error("El monto debe ser mayor a cero."); return; }
    setSaving(true);
    try {
      const r = await api.post("/finanzas/gastos", { ...form, monto: Number(form.monto) });
      toast.success(`Gasto registrado. Póliza ${r.data.poliza?.referencia || ""}`);
      setOpen(false);
      setForm({ ...form, monto: 0, proveedor: "", descripcion: "" });
      onCreated?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="btn-nuevo-gasto">
          <Plus className="mr-2 h-4 w-4" /> Registrar gasto
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nuevo gasto operativo</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Categoría *</Label>
            <Select value={form.categoria} onValueChange={set("categoria")}>
              <SelectTrigger data-testid="gto-categoria">
                <SelectValue placeholder="Selecciona categoría…" />
              </SelectTrigger>
              <SelectContent className="max-h-96">
                {CATEGORIAS_GRUPOS.map((g) => (
                  <SelectGroup key={g.label}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.items.map((i) => (
                      <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Monto (MXN) *</Label>
              <MoneyInput
                data-testid="gto-monto"
                value={form.monto} onChange={set("monto")}
              />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input data-testid="gto-fecha" type="date" value={form.fecha} onChange={set("fecha")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Forma de pago</Label>
              <Select value={form.forma} onValueChange={set("forma")}>
                <SelectTrigger data-testid="gto-forma"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMAS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Banco origen (si aplica)</Label>
              <Input data-testid="gto-banco" value={form.banco_origen} onChange={set("banco_origen")} />
            </div>
          </div>
          <div>
            <Label>Proveedor</Label>
            <Input
              data-testid="gto-proveedor"
              value={form.proveedor} onChange={set("proveedor")}
              placeholder="Nombre del proveedor o razón social"
            />
          </div>
          <div>
            <Label>Descripción / Concepto</Label>
            <Textarea
              data-testid="gto-descripcion"
              value={form.descripcion} onChange={set("descripcion")}
              rows={2}
              placeholder="Detalles del gasto"
            />
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-[12px] text-sky-900">
            <div className="font-medium">Póliza automática que se generará:</div>
            <div className="mt-1 font-mono">
              Dr <b>Cuenta de gasto</b> · Cr <b>{form.forma === "efectivo" ? "1101 Caja" : "1102 Bancos"}</b>
              {" — "}${Number(form.monto || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="gto-guardar-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar gasto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GastosOperativos() {
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroDesde, setFiltroDesde] = useState("");
  const [filtroHasta, setFiltroHasta] = useState("");

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroDesde) params.fecha_desde = filtroDesde;
      if (filtroHasta) params.fecha_hasta = filtroHasta;
      const r = await api.get("/finanzas/gastos", { params });
      setGastos(r.data.gastos || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const eliminarGasto = async (g) => {
    if (!window.confirm(`¿Cancelar el gasto ${CATEGORIA_LABEL[g.categoria] || g.categoria} de ${mxn(g.monto)}? Esto elimina la póliza asociada.`)) return;
    try {
      await api.delete(`/finanzas/gastos/${g.id}`);
      toast.success("Gasto cancelado y póliza eliminada");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Resumen del mes en curso por categoría
  const resumenMes = useMemo(() => {
    const mesActual = new Date().toISOString().slice(0, 7);
    const enMes = gastos.filter((g) => (g.fecha || "").slice(0, 7) === mesActual);
    const porGrupo = {};
    CATEGORIAS_GRUPOS.forEach((g) => { porGrupo[g.label] = 0; });
    for (const g of enMes) {
      const grupo = CATEGORIAS_GRUPOS.find((gr) => gr.items.some((i) => i.key === g.categoria))?.label || "Otros";
      porGrupo[grupo] = (porGrupo[grupo] || 0) + (g.monto || 0);
    }
    const total = enMes.reduce((s, g) => s + (g.monto || 0), 0);
    return { total, porGrupo, mes: mesActual, cantidad: enMes.length };
  }, [gastos]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        title="Gastos operativos"
        subtitle="Registro de gastos por categoría. Cada gasto dispara una póliza Dr cuenta de gasto · Cr Bancos/Caja."
      />

      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Gastos del mes {resumenMes.mes}</div>
              <div className="font-heading text-2xl font-semibold text-slate-900" data-testid="stat-gto-mes-total">
                {mxn(resumenMes.total)}
              </div>
              <div className="text-xs text-slate-500">{resumenMes.cantidad} movimientos</div>
            </div>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {CATEGORIAS_GRUPOS.map((g) => (
              <div key={g.label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{g.label}</div>
                <div className="text-sm font-medium text-slate-900" data-testid={`stat-gto-grupo-${g.label.toLowerCase()}`}>
                  {mxn(resumenMes.porGrupo[g.label] || 0)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-slate-500">Desde</Label>
            <Input type="date" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} className="w-40" data-testid="gto-filtro-desde" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Hasta</Label>
            <Input type="date" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} className="w-40" data-testid="gto-filtro-hasta" />
          </div>
          <Button variant="outline" onClick={cargar} data-testid="gto-filtrar-btn">Filtrar</Button>
          <div className="ml-auto">
            <DialogNuevoGasto onCreated={cargar} />
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Forma</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Cuenta</TableHead>
                <TableHead>Póliza</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell></TableRow>
              )}
              {!loading && gastos.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">
                  Sin gastos registrados en el rango seleccionado.
                </TableCell></TableRow>
              )}
              {gastos.map((g) => (
                <TableRow key={g.id} data-testid={`row-gto-${g.id.slice(0,6)}`}>
                  <TableCell>{g.fecha}</TableCell>
                  <TableCell className="text-xs">{CATEGORIA_LABEL[g.categoria] || g.categoria}</TableCell>
                  <TableCell>{g.proveedor || "—"}</TableCell>
                  <TableCell className="max-w-xs truncate">{g.descripcion || "—"}</TableCell>
                  <TableCell><Badge variant="outline">{g.forma}</Badge></TableCell>
                  <TableCell className="text-right font-medium">{mxn(g.monto)}</TableCell>
                  <TableCell className="font-mono text-xs">{g.cuenta_gasto}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{g.poliza_ref}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => eliminarGasto(g)}
                      data-testid={`btn-eliminar-gasto-${g.id.slice(0,6)}`}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
